import React from 'react';
import { string, arrayOf, shape } from 'prop-types';

import Text from 'crehana-ui/Text';
import { baseMain, white } from 'crehana-ui/styles/colors';
import { useTranslation } from 'react-i18next';
import EVENTS from 'Jsx/Shared/Tracking/events';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import { MAIN_FOOTER_KEY } from 'Jsx/Utils/transformKeys';
import localesEs from 'Jsx/MainFooter/locales/es';
import Lazyload from 'Jsx/Shared/Lazyload';

/* Icons */
import Twitter from 'crehana-icons/Twitter';
import Facebook from 'crehana-icons/Facebook';
import Instagram from 'crehana-icons/Instagram';
import Linkedin from 'crehana-icons/Linkedin';
import Youtube from 'crehana-icons/Youtube';
import GooglePlayIcon from 'crehana-icons/GooglePlayIcon';
import Apple from 'crehana-icons/Apple';

import Envelope from 'crehana-icons/Envelope';

import PaymentLogos from '../PaymentLogos';
import CurrencyDropdownSelector from '../CurrencyDropdownSelector/CurrencyDropdownSelector';

import { COMMUNITY, US, itemsWithPrefix } from '../../constants';

import {
  Container,
  ColumnWrapper,
  Columns,
  Link,
  CopyrightAndLegals,
  LegalsSide,
  LegalLink,
  HelpLink,
  HelpSide,
  PaymentsSide,
  HelpAndPayments,
  Line,
} from './RegularFooter.styles';

export const SOCIAL_NETWORKS = [
  {
    link: 'https://www.facebook.com/Crehanacom/',
    Icon: Facebook,
  },
  {
    link: 'https://twitter.com/crehanacom',
    Icon: Twitter,
  },
  {
    link: 'https://www.instagram.com/crehanacom/',
    Icon: Instagram,
  },
  {
    link: 'https://www.linkedin.com/school/crehana/',
    Icon: Linkedin,
  },
  {
    link: 'https://www.youtube.com/channel/UCg6_yPdtalblZ0Peqjv5rPg/featured',
    Icon: Youtube,
  },
];

const Column = ({ items, title, prefix, color, isDark }) => (
  <ColumnWrapper>
    <div>
      <Text
        color={`${isDark ? 'white' : 'base-main'}`}
        type="subtitle3"
        className="block mb-16"
        style={{ fontSize: 11 }}
      >
        {title.toUpperCase()}
      </Text>
    </div>
    <div>
      {items.map((el, index) => (
        <Link key={index} href={el.link}>
          <Text
            color={`${isDark ? 'white' : 'base-main'}`}
            type="body4"
            style={{ letterSpacing: -0.323496 }}
          >
            {prefix} {el.label}
          </Text>
        </Link>
      ))}
    </div>
  </ColumnWrapper>
);

const RegularFooter = ({
  countryPrefix,
  customTextColor,
  siteUrl,
  categories,
  softwares,
  isDark,
}) => {
  const { t } = useTranslation(MAIN_FOOTER_KEY);

  return (
    <div>
      <Container>
        <Columns>
          {countryPrefix !== 'br' && (
            <Column
              title={t('COMMUNITY_TITLE', {
                defaultValue: localesEs.es.translation.COMMUNITY_TITLE,
              })}
              items={itemsWithPrefix(COMMUNITY(t), countryPrefix, siteUrl)}
              prefix=""
              color={customTextColor}
              isDark={isDark}
            />
          )}

          <Column
            title="Categorías"
            items={categories}
            prefix="Cursos de"
            color={customTextColor}
            isDark={isDark}
          />
          <Column
            title="Software"
            items={softwares}
            prefix="Cursos de"
            color={customTextColor}
            isDark={isDark}
          />
          <ColumnWrapper>
            <Text
              type="label"
              color={isDark ? 'white' : 'base-lighter'}
              className="mb-24 block"
            >
              {t('FOOTER_DOWNLOAD', {
                defaultValue: localesEs.es.translation.FOOTER_DOWNLOAD,
              })}
            </Text>
            <div style={{ minWidth: 258 }}>
              <PrimaryButton
                onClick={() => {
                  window.track(EVENTS.DOWNLOAD_APPS.PLAY_STORE);
                  window.open(
                    'https://play.google.com/store/apps/details?id=com.crehana.android',
                    '_blank',
                  );
                }}
                label="Google play"
                leftIcon={{
                  component: GooglePlayIcon,
                  size: 18,
                  color: 'white',
                }}
                className="mb-20"
                darkMode={isDark}
                full
              />
              <PrimaryButton
                onClick={() => {
                  window.track(EVENTS.DOWNLOAD_APPS.APP_STORE);
                  window.open(
                    'https://itunes.apple.com/us/app/crehana/id1353031284',
                    '_blank',
                  );
                }}
                darkMode={isDark}
                label="App store"
                leftIcon={{
                  component: Apple,
                  size: 19,
                  color: 'white',
                }}
                labelClassName="mt-4"
                full
              />
            </div>
          </ColumnWrapper>
        </Columns>
      </Container>

      <HelpAndPayments>
        <HelpSide>
          <HelpLink href="#">
            <Envelope size={16} color={isDark ? white : customTextColor} />
            <Text type="body4" color={isDark ? 'white' : 'base-main'}>
              {countryPrefix === 'br' ? 'oi@crehana.com' : 'hola@crehana.com'}
            </Text>
          </HelpLink>
          <HelpLink href="https://crehana.zendesk.com/hc/es">
            <Text type="body4" color={isDark ? 'white' : 'base-main'}>
              {t('FOOTER_HELP', {
                defaultValue: localesEs.es.translation.FOOTER_HELP,
              })}
            </Text>
          </HelpLink>
          <div className="flex ml-24">
            {SOCIAL_NETWORKS.map((el, index) => (
              <Text
                key={index}
                type="body4"
                href={el.link}
                target="_blank"
                as="a"
                className="flex items-center mr-24"
              >
                <el.Icon
                  size={18}
                  color={isDark ? white : baseMain}
                  className="mr-8"
                />
              </Text>
            ))}
          </div>
        </HelpSide>

        <PaymentsSide>
          <Lazyload placeholder={<div style={{ height: 68 }} />}>
            <div className="flex items-center flex-no-wrap justify-center overflow-hidden">
              <PaymentLogos classNameItem="ml-16" />
            </div>
          </Lazyload>
          <div className="ml-32">
            <Lazyload placeholder={<div className="h-56" />}>
              <CurrencyDropdownSelector isDark={isDark} />
            </Lazyload>
          </div>
        </PaymentsSide>
      </HelpAndPayments>

      <Line />
      <CopyrightAndLegals id="main-footer-copy-and-legals">
        <Text type="body4" color="gray-dark" style={{ fontSize: 12 }}>
          Copyright © Crehana {new Date().getFullYear()} -
          {t('FOOTER_RIGHTS', {
            defaultValue: localesEs.es.translation.FOOTER_RIGHTS,
          })}
        </Text>
        <LegalsSide>
          {US(t, countryPrefix).map((el, index) => (
            <LegalLink key={index} href={`${siteUrl}${el.link}`}>
              <Text type="legals" color="base-lighter">
                {el.label}
              </Text>
            </LegalLink>
          ))}
        </LegalsSide>
      </CopyrightAndLegals>
    </div>
  );
};

RegularFooter.defaultProps = {
  countryPrefix: '',
  customTextColor: baseMain,
  siteUrl: '',
};

RegularFooter.propTypes = {
  countryPrefix: string,
  customTextColor: string,
  siteUrl: string,
};

Column.defaultProps = {
  color: baseMain,
  items: [],
  title: '',
  prefix: '',
};

Column.propTypes = {
  items: arrayOf(shape({})),
  title: string,
  prefix: string,
  color: string,
};

export default RegularFooter;
